import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, 
  Calendar, 
  ArrowUpCircle, 
  ArrowDownCircle, 
  Receipt, 
  FileText, 
  RefreshCw,
  Download,
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Wallet
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSACTION_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Receipt', label: 'Receipt' },
  { value: 'Custom_Invoice', label: 'Custom Invoice' },
  { value: 'Refund', label: 'Refund' },
  { value: 'Adjustment', label: 'Adjustment' }
];

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_credits: 0, total_debits: 0, net_flow: 0 });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, [page, startDate, endDate, transactionType]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (transactionType !== 'all') params.append('transaction_type', transactionType);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/admin/transactions?${params.toString()}`);
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchTransactions();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setTransactionType('all');
    setPage(1);
  };

  const viewTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'credit':
        return <ArrowDownCircle className="h-4 w-4 text-green-600" />;
      case 'debit':
        return <ArrowUpCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Receipt className="h-4 w-4 text-slate-600" />;
    }
  };

  const getTransactionTypeBadge = (txnType) => {
    const colors = {
      'Invoice': 'bg-blue-100 text-blue-800',
      'Receipt': 'bg-green-100 text-green-800',
      'Custom_Invoice': 'bg-purple-100 text-purple-800',
      'Refund': 'bg-orange-100 text-orange-800',
      'Adjustment': 'bg-slate-100 text-slate-800'
    };
    return (
      <Badge className={colors[txnType] || 'bg-slate-100 text-slate-800'}>
        {txnType?.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" data-testid="transactions-title">
              Transactions
            </h1>
            <p className="text-slate-500 mt-1">View and manage all transactions across the platform</p>
          </div>
          <Button onClick={fetchTransactions} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 min-[576px]:grid-cols-2 xl:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Transactions</p>
                  <p className="text-2xl font-bold text-blue-900">{total}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Credits</p>
                  <p className="text-2xl font-bold text-green-900">₹{summary.total_credits?.toLocaleString()}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Total Debits</p>
                  <p className="text-2xl font-bold text-red-900">₹{summary.total_debits?.toLocaleString()}</p>
                </div>
                <TrendingDown className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Net Flow</p>
                  <p className={`text-2xl font-bold ${summary.net_flow >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                    ₹{summary.net_flow?.toLocaleString()}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[250px]">
                <Label className="text-sm text-slate-600 mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Transaction ID, Email, Mobile, Company..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="flex gap-2">
                <div>
                  <Label className="text-sm text-slate-600 mb-1 block">From Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                    className="w-40"
                    data-testid="start-date"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600 mb-1 block">To Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                    className="w-40"
                    data-testid="end-date"
                  />
                </div>
              </div>

              {/* Transaction Type */}
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Type</Label>
                <Select value={transactionType} onValueChange={(v) => { setTransactionType(v); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
                  <Filter className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="bg-white border-0 shadow-sm" data-testid="transactions-table">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-16">
                <Receipt className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No transactions found</p>
                <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Transaction ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Type</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 text-nowrap">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Date</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-slate-50 transition-colors" data-testid={`txn-row-${txn.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-mono text-sm text-slate-700">{txn.id.substring(0, 8)}...</p>
                            {txn.billing_id && (
                              <p className="text-xs text-slate-500 text-nowrap">Billing: {txn.billing_id}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {txn.user ? (
                            <div>
                              <p className="font-medium text-slate-800">{txn.user.company_name || txn.user.email}</p>
                              <p className="text-xs text-slate-500">{txn.user.email}</p>
                              {txn.user.phone && (
                                <p className="text-xs text-slate-500">{txn.user.phone}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(txn.type)}
                            <span className={`font-medium capitalize ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {txn.type}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'credit' ? '+' : '-'}₹{txn.amount?.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getTransactionTypeBadge(txn.transaction_type)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 text-nowrap">
                          {formatDate(txn.created_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewTransactionDetails(txn)}
                            data-testid={`view-txn-${txn.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, total)} of {total} transactions
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="next-page"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Transaction Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-blue-600" />
                Transaction Details
              </DialogTitle>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Transaction ID</p>
                    <p className="font-mono text-sm">{selectedTransaction.id}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Billing ID</p>
                    <p className="font-mono text-sm">{selectedTransaction.billing_id || 'N/A'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Type</p>
                    <div className="flex items-center gap-2">
                      {getTypeIcon(selectedTransaction.type)}
                      <span className="capitalize font-medium">{selectedTransaction.type}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Category</p>
                    {getTransactionTypeBadge(selectedTransaction.transaction_type)}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Amount</p>
                    <p className={`text-xl font-bold ${selectedTransaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedTransaction.type === 'credit' ? '+' : '-'}₹{selectedTransaction.amount?.toLocaleString()}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Balance After</p>
                    <p className="font-medium">₹{selectedTransaction.balance_after?.toLocaleString()}</p>
                  </div>
                  <div className="space-y-1 col-span-2">
                    <p className="text-xs text-slate-500">Date & Time</p>
                    <p className="text-sm">{formatDate(selectedTransaction.created_at)}</p>
                  </div>
                </div>

                {selectedTransaction.user && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">User Information</p>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-lg">
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Company</p>
                        <p className="text-sm font-medium">{selectedTransaction.user.company_name || 'N/A'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Role</p>
                        <p className="text-sm capitalize">{selectedTransaction.user.role?.replace('_', ' ')}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="text-sm">{selectedTransaction.user.email}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Phone</p>
                        <p className="text-sm">{selectedTransaction.user.phone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-xs text-slate-500 mb-1">Description</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedTransaction.description}</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
